import { FC, useState, type FormEvent, useMemo } from "react";
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DataTable,
  Column,
  PhoneInput,
  cn,
} from "@racoon/ui";
import { IcPlus, IcPencil, IcAiOn, IcChevron } from "@racoon/icons";
import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";

import {
  useListContactsQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
} from "~/shared/api/contact/contact.grpc";
import { useUpdateContainerGroupMutation } from "~/shared/api/container-group/container-group.grpc";

type Contact = ConnectProto.Contact;

// A contact answers for this site when it is linked to container groups; an
// empty list makes it a contact of the customer as a whole. Contacts staged
// before the location exists carry an empty list too, so they read as
// customer-wide until Save attaches them to groups.
const answersForSite = (contact: Contact): boolean => contact.containerGroupIds.length > 0;

export const ContactsBlock: FC<{
  locationId?: number;
  organizationId?: number;
  pendingContacts?: Contact[];
  setPendingContacts?: (contacts: Contact[] | ((prev: Contact[]) => Contact[])) => void;
  isCustomerCard?: boolean;
  isGroupsLoading?: boolean;
  primaryContactId?: number;
  onPrimaryChange?: (id: number) => void;
  containerGroups?: any[];
}> = ({
  locationId,
  organizationId,
  pendingContacts = [],
  setPendingContacts,
  isCustomerCard,
  isGroupsLoading,
  primaryContactId,
  onPrimaryChange,
  containerGroups = [],
}) => {
  const t = useT();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [activeOrgIdForNewContact, setActiveOrgIdForNewContact] = useState<number | null>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<Record<number, boolean>>({});
  const [formData, setFormData] = useState<Partial<Contact>>({
    position: "",
    name: "",
    phone: "",
    email: "",
  });

  const groupIdsArray = containerGroups.map((cg: any) => Number(cg.id));

  const requestOrgIds = useMemo(() => {
    const ids = new Set<number>();
    if (organizationId) ids.add(organizationId);
    containerGroups.forEach((cg: any) => {
      const orgId = Number(cg.ownerOrganizationId || cg.owner_organization_id);
      if (orgId) ids.add(orgId);
    });
    return Array.from(ids);
  }, [organizationId, containerGroups]);

  const queryParams = {
    ownerOrganizationIds: requestOrgIds.map(BigInt),
    containerGroupIds: groupIdsArray.map(BigInt),
  };

  // currentData/isFetching, not data/isLoading: RTK Query keeps the previous
  // args' result in `data` while it refetches, and `isLoading` is true only for
  // the first request ever. Stepping to another location through Previous/Next
  // would otherwise leave the last one's rows on screen — and they are editable,
  // so a dispatcher could open one and update a contact of the wrong site.
  const { currentData: contactsResponse, isFetching: isContactsFetching } = useListContactsQuery(
    queryParams,
    { skip: requestOrgIds.length === 0 && groupIdsArray.length === 0 },
  );

  const apiContacts = contactsResponse?.contacts || [];
  const allContacts = [...apiContacts, ...pendingContacts];

  const [updateContainerGroup] = useUpdateContainerGroupMutation();
  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();
  const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation();

  const [localPrimaryOverrides, setLocalPrimaryOverrides] = useState<Record<number, number>>({});

  const primaryContactIds = useMemo(() => {
    const map: Record<number, number> = {};
    if (organizationId && primaryContactId) {
      map[organizationId] = primaryContactId;
    }
    containerGroups.forEach((cg: any) => {
      const orgId = Number(cg.ownerOrganizationId || cg.owner_organization_id);
      const primaryId = Number(cg.primaryContactId || cg.primary_contact_id);
      if (orgId && primaryId) map[orgId] = primaryId;
    });

    return { ...map, ...localPrimaryOverrides };
  }, [containerGroups, organizationId, primaryContactId, localPrimaryOverrides]);

  const handlePrimaryChangeLocal = async (contactId: number, orgId: number) => {
    setLocalPrimaryOverrides((prev) => ({ ...prev, [orgId]: contactId }));

    if (onPrimaryChange && Number(orgId) === Number(organizationId)) {
      onPrimaryChange(contactId);
    }
    if (contactId <= 0) return;

    const group = containerGroups.find(
      (cg: any) => Number(cg.ownerOrganizationId || cg.owner_organization_id) === Number(orgId),
    );
    if (group && group.id) {
      try {
        await updateContainerGroup({
          id: BigInt(group.id),
          primaryContactId: BigInt(contactId),
        }).unwrap();
      } catch (err) {
        console.error("Failed to update primary contact", err);
      }
    }
  };

  const orgNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    containerGroups.forEach((cg: any) => {
      const orgId = Number(cg.ownerOrganizationId || cg.owner_organization_id);
      const org = cg.ownerOrganization || cg.owner_organization;
      if (orgId && org?.name) {
        map[orgId] = org.name;
      }
    });
    return map;
  }, [containerGroups]);

  const relevantOrgIds = useMemo(() => {
    const ids = new Set<number>();
    if (organizationId) ids.add(organizationId);
    containerGroups.forEach((cg: any) => {
      const orgId = cg.ownerOrganizationId || cg.owner_organization_id;
      if (orgId) ids.add(Number(orgId));
    });
    allContacts.forEach((c: any) => {
      const orgId = c.ownerOrganizationId || c.owner_organization_id;
      if (orgId) ids.add(Number(orgId));
    });
    return Array.from(ids);
  }, [organizationId, containerGroups, allContacts]);

  const groupedContacts = useMemo(() => {
    const map = new Map<number, any>();

    allContacts.forEach((c: any) => {
      const orgId = Number(c.owner_organization_id || c.ownerOrganizationId);
      if (orgId) {
        if (!map.has(orgId)) map.set(orgId, []);
        map.get(orgId)!.push(c);
      }
    });

    map.forEach((contactsArray) => {
      contactsArray.sort((a: Contact, b: Contact) => {
        const aIsLocal = answersForSite(a);
        const bIsLocal = answersForSite(b);

        if (aIsLocal && !bIsLocal) return -1;
        if (!aIsLocal && bIsLocal) return 1;
        return 0;
      });
    });

    return map;
  }, [allContacts]);

  const toggleOrg = (orgId: number) => setExpandedOrgs((p) => ({ ...p, [orgId]: !p[orgId] }));

  const resetForm = () => {
    setFormData({ name: "", position: "", phone: "", email: "" });
    setEditingContact(null);
    setActiveOrgIdForNewContact(null);
  };

  const handleCreate = (orgId: number) => {
    resetForm();
    setActiveOrgIdForNewContact(orgId);
    setIsModalOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      position: contact.position || "",
      name: contact.name,
      phone: contact.phone || "",
      email: contact.email || "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!editingContact) return;

    try {
      if (editingContact.id < 0 && setPendingContacts) {
        setPendingContacts((prev) => prev.filter((c) => c.id !== editingContact.id));
      } else {
        await deleteContact({
          id: BigInt(editingContact.id),
          ownerOrganizationId: BigInt(editingContact.ownerOrganizationId),
        }).unwrap();
      }
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to delete contact:", error);
    }
  };

  // Compared as decimal strings rather than through Number(), since this decides
  // whose groups a contact is attached to. The organizationId prop is itself a
  // number, so the comparison is only as exact as what reaches it - which is
  // fine while every id column in the schema is a 32-bit integer.
  const containerGroupIdsOf = (orgId: number): bigint[] =>
    containerGroups
      .filter(
        (cg: any) => String(cg.ownerOrganizationId ?? cg.owner_organization_id) === String(orgId),
      )
      .map((cg: any) => BigInt(cg.id));

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!formData.name || isGroupsLoading) return;

    // Only a new contact needs an organization picked for it. An edit keeps the
    // one already on the contact, so requiring it here would block editing on
    // any location that has no client organization set.
    const targetOrgId = activeOrgIdForNewContact || organizationId;
    if (!editingContact && !targetOrgId) return;

    // A staged contact has no server row to update — its negative placeholder id
    // would not resolve — so an edit rewrites it in place in the pending list,
    // mirroring how delete removes it from there. Checked before the branch that
    // stages new contacts, which would otherwise add a duplicate.
    if (editingContact && editingContact.id < 0 && setPendingContacts) {
      setPendingContacts((prev) =>
        prev.map((contact) =>
          contact.id === editingContact.id
            ? ({
                ...contact,
                name: formData.name,
                phone: formData.phone,
                email: formData.email,
                position: formData.position,
              } as Contact)
            : contact,
        ),
      );
      setIsModalOpen(false);
      resetForm();
      return;
    }

    // A new contact on a location that does not exist yet is staged until the
    // card's save creates the location and, with it, the contacts.
    if (!editingContact && !locationId && !isCustomerCard && setPendingContacts) {
      setPendingContacts((prev) => [
        ...prev,
        {
          id: -Date.now(),
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          position: formData.position,
          ownerOrganizationId: targetOrgId,
          containerGroupIds: [],
        } as any,
      ]);
      setIsModalOpen(false);
      resetForm();
      return;
    }

    try {
      if (editingContact) {
        // Same as delete: the permission check reads owner_organization_id off
        // the request, so an update without it is rejected before it runs.
        await updateContact({
          id: BigInt(editingContact.id),
          ownerOrganizationId: BigInt(editingContact.ownerOrganizationId),
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          position: formData.position,
        }).unwrap();
      } else {
        // Unreachable — the guard above returns when a new contact has no
        // organization — but it is what narrows `targetOrgId` for `BigInt`.
        if (!targetOrgId) return;
        // Empty when the customer keeps no groups here: a contact of the
        // customer as a whole.
        await createContact({
          ownerOrganizationId: BigInt(targetOrgId),
          containerGroupIds: containerGroupIdsOf(targetOrgId),
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          position: formData.position,
        }).unwrap();
      }
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to save contact:", error);
    }
  };

  const columns: Column<Contact>[] = useMemo(
    () => [
      {
        id: "name",
        header: t(Namespace.PickupLocation, "tableFullName"),
        cell: (info) => {
          const contact = info.row.original as Contact;
          const isGrey = !isCustomerCard && !answersForSite(contact);

          return <span className={cn(isGrey && "text-[#727986]")}>{contact.name}</span>;
        },
      },
      {
        id: "position",
        header: t(Namespace.PickupLocation, "tablePosition"),
        cell: (info) => {
          const contact = info.row.original as Contact;
          const isGrey = !isCustomerCard && !answersForSite(contact);

          return <span className={cn(isGrey && "text-[#727986]")}>{contact.position || "—"}</span>;
        },
      },
      {
        id: "phone",
        header: t(Namespace.PickupLocation, "tableContactPhone"),
        cell: (info) => {
          const contact = info.row.original as Contact;
          const phone = contact.phone;
          const isGrey = !isCustomerCard && !answersForSite(contact);

          if (!phone) return <span className="text-[#727986]">—</span>;

          return (
            <a
              href={`https://wa.me/${phone.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className={cn("hover:underline", isGrey ? "text-[#727986]" : "text-brand-blue")}
            >
              {phone}
            </a>
          );
        },
      },
      {
        id: "priority",
        header: () => (
          <div className="flex justify-center">
            <IcAiOn className="w-6 h-6 text-[#727986]" />
          </div>
        ),
        cell: (info) => {
          const contact = info.row.original as any;
          const orgId = Number(contact.owner_organization_id || contact.ownerOrganizationId);
          const isPrimary = primaryContactIds[orgId] === Number(contact.id);
          return (
            <div className="flex items-center justify-center">
              <input
                type="radio"
                name={`primary-contact-${orgId}`}
                className="w-4 h-4 cursor-pointer accent-brand-blue"
                checked={isPrimary}
                onChange={() => handlePrimaryChangeLocal(Number(contact.id), orgId)}
              />
            </div>
          );
        },
        width: 60,
      },
      {
        id: "actions",
        header: "",
        // Every listed contact is editable, including the organization-level
        // ones this card can itself create. They stay greyed in the other
        // columns: the tint means "shared with every location of this
        // organization", so an edit here is felt everywhere — not "read only".
        cell: (info) => {
          return (
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 ml-auto"
              onClick={() => handleEdit(info.row.original)}
            >
              <IcPencil width={18} height={18} className="text-brand-blue" />
            </button>
          );
        },
        width: 50,
      },
    ],
    [primaryContactIds, handlePrimaryChangeLocal, isCustomerCard, t],
  );

  // Saving while the groups are still loading would send an empty set and make
  // a customer-wide contact on a location that has groups.
  const isLoadingGlobal = isCreating || isUpdating || isDeleting || !!isGroupsLoading;

  return (
    <div className="w-full h-full overflow-auto pr-2 pb-4">
      {isContactsFetching ? (
        <div className="p-8 text-center text-muted-foreground">
          {t(Namespace.PickupLocation, "loadingContacts")}
        </div>
      ) : (
        <>
          {relevantOrgIds.length === 0 && (
            <div className="p-8 text-center text-gray-400 border border-dashed rounded-xl mt-4">
              {t(Namespace.PickupLocation, "selectCustomerOrAddGroups")}
            </div>
          )}

          {relevantOrgIds.map((orgId) => {
            const orgName = isCustomerCard
              ? t(Namespace.PickupLocation, "organizationContacts")
              : orgNamesMap[orgId] || t(Namespace.PickupLocation, "organizationId", { id: orgId });

            const isExpanded = expandedOrgs[orgId] ?? false;
            const orgContacts = groupedContacts.get(orgId) || [];

            return (
              <div
                key={orgId}
                className="border border-gray-200 rounded-xl bg-[#F8F9FA] mb-4 overflow-hidden"
              >
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer select-none"
                  onClick={() => toggleOrg(orgId)}
                >
                  <div className="flex items-center gap-3 font-semibold text-gray-900 text-base">
                    <IcChevron
                      className={cn(
                        "w-5 h-5 text-gray-500 transition-transform duration-200",
                        isExpanded ? "rotate-180" : "rotate-0",
                      )}
                    />
                    {orgName}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-brand-blue hover:bg-brand-blue/10 gap-1.5 h-8 font-medium !text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreate(orgId);
                    }}
                  >
                    <IcPlus className="w-6 h-6" />
                    {t(Namespace.PickupLocation, "newContact")}
                  </Button>
                </div>

                {isExpanded && (
                  <div className="px-5 pb-5">
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                      <DataTable
                        columns={columns}
                        data={orgContacts}
                        empty={
                          <div className="py-8 text-center text-gray-400">
                            {t(Namespace.PickupLocation, "noContacts")}
                          </div>
                        }
                        className="border-0"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-full sm:w-[40rem]">
          <DialogHeader className="!p-0">
            <DialogTitle>
              {editingContact
                ? t(Namespace.PickupLocation, "editContact")
                : t(Namespace.PickupLocation, "newContact")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>{t(Namespace.PickupLocation, "fullName")}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t(Namespace.PickupLocation, "position")}</Label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData((p) => ({ ...p, position: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t(Namespace.PickupLocation, "contactPhone")}</Label>
              <PhoneInput
                id="contact-phone"
                value={formData.phone || ""}
                onValueChange={(val) => setFormData((p) => ({ ...p, phone: val }))}
                defaultCountry="KZ"
                allowedCountries={["KZ", "RU", "US"]}
                wrapperClassName="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label>{t(Namespace.PickupLocation, "email")}</Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
              />
            </div>

            <DialogFooter className="flex sm:justify-between w-full items-center">
              {editingContact ? (
                <Button
                  id="edit-pickup-location-archive-button"
                  type="button"
                  variant="opacityRedWithOutline"
                  onClick={handleDelete}
                  disabled={isLoadingGlobal}
                >
                  {t(Namespace.PickupLocation, "delete") || "Удалить"}
                </Button>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isLoadingGlobal}
                >
                  {t(Namespace.PickupLocation, "cancel")}
                </Button>
                <Button type="submit" disabled={isLoadingGlobal}>
                  {t(Namespace.PickupLocation, "save")}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
